import { useState } from 'react';

import { cn } from '@/lib/cn';

interface UserAvatarProps {
  userId: string;
  /** Optional display name / nickname — used to derive the fallback initial. */
  label?: string;
  /**
   * Optional avatar image URL (Wave 2 — from the synced user profile). When
   * present and loadable it replaces the initial; on load error we fall back
   * to the deterministic colour-wheel initial.
   */
  avatarUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE: Record<NonNullable<UserAvatarProps['size']>, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-base',
};

/**
 * Placeholder avatar for QSocials user references. Wave 1 does not have a
 * profile-lookup endpoint — qsocials only stores Logto `sub`s — so we render
 * a deterministic initial + colour wheel from the sub string. Wave 2 swaps
 * this out for real avatars (fitp-api `/users/:id` once IDs are reconciled).
 */
function pickColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  // Slice through a small QLASH-tinted palette.
  const palette = [
    'bg-brand/25 text-brand',
    'bg-sky-500/20 text-sky-300',
    'bg-violet-500/20 text-violet-300',
    'bg-rose-500/20 text-rose-300',
    'bg-amber-500/20 text-amber-300',
    'bg-emerald-500/20 text-emerald-300',
  ];
  return palette[h % palette.length];
}

function initialOf(label: string | undefined, fallback: string): string {
  const src = (label && label.trim()) || fallback;
  return src.slice(0, 1).toUpperCase();
}

export function UserAvatar({
  userId,
  label,
  avatarUrl,
  size = 'md',
  className,
}: UserAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const colorCls = pickColor(userId);

  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={label ?? ''}
        aria-hidden
        loading="lazy"
        onError={() => setImgFailed(true)}
        className={cn(
          'inline-block shrink-0 rounded-full object-cover',
          SIZE[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold',
        SIZE[size],
        colorCls,
        className,
      )}
    >
      {initialOf(label, userId)}
    </span>
  );
}
